import { supabase } from "@/integrations/supabase/client";

export interface ReservationGuest {
  id?: string;
  reservation_id: string;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  created_at?: string;
  updated_at?: string;
}

export interface CheckIn {
  id?: string;
  reservation_id: string;
  unique_token?: string;
  main_guest_document_path?: string;
  bed_configuration?: 'separated' | 'joined';
  completed_at?: string;
  created_at?: string;
  updated_at?: string;
}

export const getCheckInByToken = async (token: string) => {
  const { data, error } = await supabase
    .from('check_ins')
    .select('*')
    .eq('unique_token', token)
    .single();
    
  if (error) throw error;
  return data as CheckIn;
};

export const getCheckInByReservationId = async (reservationId: string) => {
  const { data, error } = await supabase
    .from('check_ins')
    .select('*')
    .eq('reservation_id', reservationId)
    .maybeSingle();
    
  if (error) throw error;
  return data as CheckIn | null;
};

export const createCheckIn = async (reservationId: string) => {
  const { data, error } = await supabase
    .from('check_ins')
    .insert({ reservation_id: reservationId })
    .select()
    .single();
    
  if (error) throw error;
  return data as CheckIn;
};

export const updateCheckIn = async (id: string, updates: Partial<CheckIn>) => {
  const { data, error } = await supabase
    .from('check_ins')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
    
  if (error) throw error;
  return data as CheckIn;
};

export const completeCheckIn = async (id: string) => {
  const { data, error } = await supabase
    .from('check_ins')
    .update({ completed_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();
    
  if (error) throw error;
  return data as CheckIn;
};

export const getReservationGuests = async (reservationId: string) => {
  const { data, error } = await supabase
    .from('reservation_guests')
    .select('*')
    .eq('reservation_id', reservationId)
    .order('created_at', { ascending: true });
    
  if (error) throw error;
  return data as ReservationGuest[];
};

export const addReservationGuest = async (guest: Omit<ReservationGuest, 'id' | 'created_at' | 'updated_at'>) => {
  const { data, error } = await supabase
    .from('reservation_guests')
    .insert(guest)
    .select()
    .single();
    
  if (error) throw error;
  return data as ReservationGuest;
};

export const deleteReservationGuest = async (id: string) => {
  const { error } = await supabase
    .from('reservation_guests')
    .delete()
    .eq('id', id);
    
  if (error) throw error;
  return true;
};

export const uploadIdentityDocument = async (file: File, reservationId: string) => {
  const fileExt = file.name.split('.').pop();
  const fileName = `${reservationId}-${Date.now()}.${fileExt}`;
  const filePath = `${reservationId}/${fileName}`;

  const { error: uploadError } = await supabase.storage
    .from('identity-documents')
    .upload(filePath, file);

  if (uploadError) throw uploadError;

  return filePath;
};

export const getDocumentUrl = async (path: string) => {
  const { data } = await supabase.storage
    .from('identity-documents')
    .createSignedUrl(path, 3600); // 1 hour expiry

  return data?.signedUrl;
};
